import { el } from "./dom";

export const FEEDBACK_EMAIL = "feedback@strad.global";

// The live pilot contact page (Webflow) where an interested person submits THEIR
// OWN email to request pilot access. No third-party email is ever entered in the
// app; the recipient self-submits on this page.
export const SHARE_URL = "https://www.strad.global/contact/trustlens-pilot";

export const APP_VERSION = "1.0 (pilot)";

export const FEEDBACK_CATEGORIES = ["Idea", "Not working", "Other"] as const;

export interface FeedbackFields {
  category: string;
  message: string;
  // A name and reason, never an email address.
  suggest: string;
}

// Only ever UI state and version. By construction this carries no notes, people,
// ratings, or any stored content.
export interface FeedbackContext {
  screen: string;
  version: string;
}

// Build the mail subject and body from the fields. When context is provided it
// appends ONLY the screen and version. Pure, so it is testable headlessly.
export function buildFeedbackMail(
  fields: FeedbackFields,
  context: FeedbackContext | null,
): { subject: string; body: string } {
  const subject = `Trust Lens feedback: ${fields.category}`;
  const parts: string[] = [];
  if (fields.message.trim()) parts.push(fields.message.trim());
  if (fields.suggest.trim()) {
    parts.push(`Suggested contact (name and why): ${fields.suggest.trim()}`);
  }
  if (context) {
    parts.push(`---\nScreen: ${context.screen}\nVersion: ${context.version}`);
  }
  return { subject, body: parts.join("\n\n") };
}

export function mailtoHref(to: string, subject: string, body: string): string {
  return `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

// Seam: feedback is sent via mailto today; a POST sink can replace this later
// without any change to the panel UI.
export interface FeedbackSink {
  send(subject: string, body: string): void;
}

export const mailtoSink: FeedbackSink = {
  send(subject, body) {
    window.location.href = mailtoHref(FEEDBACK_EMAIL, subject, body);
  },
};

function field(label: string, control: HTMLElement): HTMLElement {
  return el(
    "div",
    { class: "settings__field" },
    label ? el("span", { class: "label", text: label }) : null,
    control,
  );
}

function overlay(): { root: HTMLElement; card: HTMLElement; close: () => void } {
  const root = el("div", { class: "consent" });
  const close = () => root.remove();
  const card = el("div", {
    class: "consent__card",
    attrs: { role: "dialog", "aria-modal": "true" },
  });
  root.append(card);
  return { root, card, close };
}

// The feedback panel. `screen` is the current route name, used only when the
// context box is ticked. Submitting composes a message and hands it to the sink.
export function openFeedbackPanel(screen: string, sink: FeedbackSink = mailtoSink): void {
  const { root, card, close } = overlay();

  const category = el("select", { class: "input select" }) as HTMLSelectElement;
  for (const c of FEEDBACK_CATEGORIES) category.append(el("option", { value: c, text: c }));

  const message = el("textarea", {
    class: "input textarea",
    placeholder: "What is working, what is not, or what you would change?",
  }) as HTMLTextAreaElement;

  const suggest = el("input", {
    class: "input",
    type: "text",
    placeholder: "Suggest someone I should speak to: a name and why (optional)",
  }) as HTMLInputElement;

  const context = el("input", { type: "checkbox" }) as HTMLInputElement; // default OFF

  const send = el("button", {
    class: "btn btn--primary",
    text: "Send feedback",
    on: {
      click: () => {
        const ctx = context.checked ? { screen, version: APP_VERSION } : null;
        const { subject, body } = buildFeedbackMail(
          { category: category.value, message: message.value, suggest: suggest.value },
          ctx,
        );
        sink.send(subject, body);
        close();
      },
    },
  });

  card.append(
    el("span", { class: "label", text: "Feedback" }),
    el("h2", { class: "consent__title", text: "Tell the STRAD team" }),
    field("Category", category),
    field("Your feedback", message),
    field("Suggest someone I should speak to", suggest),
    el(
      "label",
      { class: "radio feedback__context" },
      context,
      el("span", { text: "Include basic context (current screen, app version)" }),
    ),
    el("p", {
      class: "sensitive-note",
      text: "Feedback goes to the STRAD team. Please do not include confidential or personal information.",
    }),
    el("div", { class: "consent__actions" }, send, el("button", { class: "btn btn--ghost", text: "Cancel", on: { click: close } })),
  );
  document.body.append(root);
}

// The share panel. The user copies a link to the marketing interest form; there
// is no field to enter anyone else's email. The other person self-submits.
export function openSharePanel(): void {
  const { root, card, close } = overlay();

  const link = el("input", { class: "input", type: "text", value: SHARE_URL }) as HTMLInputElement;
  link.readOnly = true;

  const status = el("span", { class: "remaining" });

  const copy = el("button", {
    class: "btn btn--primary",
    text: "Copy link",
    on: {
      click: async () => {
        try {
          await navigator.clipboard.writeText(SHARE_URL);
          status.textContent = "Link copied.";
        } catch {
          link.select();
          status.textContent = "Press Cmd or Ctrl + C to copy.";
        }
      },
    },
  });

  card.append(
    el("span", { class: "label", text: "Share" }),
    el("h2", { class: "consent__title", text: "Share Trust Lens" }),
    el("p", {
      class: "lede",
      text: "Know someone who would find this useful? Send them this link to request pilot access:",
    }),
    field("Link", link),
    el(
      "div",
      { class: "consent__actions" },
      copy,
      el("a", {
        class: "btn btn--ghost",
        href: SHARE_URL,
        text: "Open",
        attrs: { target: "_blank", rel: "noopener noreferrer" },
      }),
      el("button", { class: "btn btn--ghost", text: "Close", on: { click: close } }),
      status,
    ),
  );
  document.body.append(root);
}
